import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { commentsService, type Comment } from '../../../services/projectManagement/commentsService';
import type { ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { getDisplayName } from '../../../services/projectManagement/membersService';

interface CommentsPanelProps {
  taskId: string;
  members?: ProjectManagementMember[];
  onUpdate?: () => void;
}

const CommentsPanel: React.FC<CommentsPanelProps> = ({ taskId, members = [], onUpdate }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionAtIndex, setMentionAtIndex] = useState<number | null>(null);
  const [mentionCursor, setMentionCursor] = useState<number | null>(null);
  const [mentionRecords, setMentionRecords] = useState<Array<{ userId: string; label: string }>>([]);

  useEffect(() => {
    void fetchComments();
  }, [taskId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const { data, error } = await commentsService.list(taskId);

      if (error) {
        toast.error('Failed to load comments');
      } else {
        setComments(data || []);
      }
    } catch (err) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const mentionUserIds = Array.from(
        new Set(
          mentionRecords
            .map((m) => String(m.userId || '').trim())
            .filter(Boolean)
        )
      );

      const { error } = await commentsService.create({
        task_id: taskId,
        content: newComment.trim(),
        mentions: mentionUserIds,
      });

      if (error) {
        toast.error('Failed to add comment');
      } else {
        toast.success('Comment added');
        setNewComment('');
        setMentionOpen(false);
        setMentionQuery('');
        setMentionAtIndex(null);
        setMentionCursor(null);
        setMentionRecords([]);
        await fetchComments();
        onUpdate?.();
      }
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const { error } = await commentsService.delete(commentId);

      if (error) {
        toast.error('Failed to delete comment');
      } else {
        toast.success('Comment deleted');
        await fetchComments();
        onUpdate?.();
      }
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getMentionContext = (text: string, cursor: number) => {
    const upto = text.slice(0, cursor);
    const at = upto.lastIndexOf('@');
    if (at === -1) return null;

    const prev = at > 0 ? upto[at - 1] : '';
    if (prev && !/\s/.test(prev)) return null;

    const q = upto.slice(at + 1);
    if (/\s/.test(q)) return null;
    return { atIndex: at, query: q };
  };

  const syncMentions = (text: string) => {
    setMentionRecords((prev) => prev.filter((r) => text.includes(r.label)));
  };

  const updateMentionState = (text: string) => {
    const el = textareaRef.current;
    const cursor = el?.selectionStart ?? text.length;
    const ctx = getMentionContext(text, cursor);
    if (!ctx) {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionAtIndex(null);
      setMentionCursor(null);
      return;
    }

    setMentionOpen(true);
    setMentionQuery(ctx.query);
    setMentionAtIndex(ctx.atIndex);
    setMentionCursor(cursor);
  };

  const mentionCandidates = (() => {
    const q = mentionQuery.trim().toLowerCase();
    const filtered = (members || [])
      .filter((m) => m?.user_id && m.user_id !== user?.id)
      .filter((m) => {
        if (!q) return true;
        const hay = `${m.full_name || ''} ${m.email || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
    return filtered;
  })();

  const insertMention = (m: ProjectManagementMember) => {
    const at = mentionAtIndex;
    if (at === null) return;

    const el = textareaRef.current;
    const cursor = mentionCursor ?? el?.selectionStart ?? newComment.length;

    const label = `@${getDisplayName(m)}`;
    const before = newComment.slice(0, at);
    const after = newComment.slice(cursor);
    const inserted = `${label} `;
    const next = `${before}${inserted}${after}`;

    setNewComment(next);
    setMentionRecords((prev) => {
      const deduped = prev.filter((r) => r.userId !== m.user_id);
      return [...deduped, { userId: m.user_id, label }].filter((r) => next.includes(r.label));
    });

    setMentionOpen(false);
    setMentionQuery('');
    setMentionAtIndex(null);
    setMentionCursor(null);

    requestAnimationFrame(() => {
      const target = textareaRef.current;
      if (!target) return;
      const pos = before.length + inserted.length;
      target.focus();
      target.setSelectionRange(pos, pos);
    });
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="text-sm text-gray-500">Loading comments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">
          Comments ({comments.length})
        </h4>
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleAddComment} className="space-y-2">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newComment}
            onChange={(e) => {
              const next = e.target.value;
              setNewComment(next);
              syncMentions(next);
              updateMentionState(next);
            }}
            onKeyUp={(e) => updateMentionState((e.target as HTMLTextAreaElement).value)}
            onClick={(e) => updateMentionState((e.target as HTMLTextAreaElement).value)}
          placeholder="Add a comment..."
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          disabled={submitting}
          />

          {mentionOpen && (
            <div className="absolute left-0 right-0 top-full mt-1 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
              {mentionCandidates.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
              ) : (
                <div className="max-h-56 overflow-y-auto">
                  {mentionCandidates.map((m) => (
                    <button
                      key={m.user_id}
                      type="button"
                      onClick={() => insertMention(m)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      <div className="font-medium text-gray-900 truncate">{getDisplayName(m)}</div>
                      {m.email ? <div className="text-xs text-gray-500 truncate">{m.email}</div> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Adding...' : 'Add Comment'}
          </button>
        </div>
      </form>

      {/* Comments List */}
      {comments.length > 0 ? (
        <div className="space-y-3">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="group rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50"
            >
              {/* Comment Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {/* User Avatar */}
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                    {comment.user_email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  {/* User Info */}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {comment.user_name || comment.user_email || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(comment.created_at)}
                    </p>
                  </div>
                </div>
                
                {/* Delete Button (only for comment author) */}
                {user?.id === comment.user_id && (
                  <button
                    onClick={() => void handleDelete(comment.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity"
                    title="Delete comment"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Comment Content */}
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {comment.content}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <div className="text-3xl mb-2">💬</div>
          <p className="text-sm text-gray-600">No comments yet</p>
          <p className="text-xs text-gray-500 mt-1">
            Be the first to share your thoughts
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentsPanel;
